import { HeaderWithCloseButton } from 'tradera/components/scrollable-modal/header-with-close-button';
import { ScrollableModalContent } from 'tradera/components/scrollable-modal/scrollable-modal-content';
import { SideMenu } from 'tradera/components/side-menu/side-menu';
import {
    type TranslationFunction,
    Trans,
    useTranslator,
    useUrlLocalizer
} from 'tradera/lang/translate';
import { useAppDispatch, useAppSelector } from 'tradera/state/hooks';
import { closeNotificationsSideSheet } from 'tradera/state/ui/reducer';
import LoadingIcon from '@tradera/blueprint/theme/icons2/loading.svg?jsx';
import ArrowRightIcon from '@tradera/blueprint/theme/icons2/arrow-right.svg?jsx';
import DotIcon from '@tradera/blueprint/theme/icons2/radio-button.svg?jsx';
import {
    endpoints,
    useGetNotificationsEndlessQuery,
    NOTIFICATIONS_ENDLESS_PAGESIZE
} from 'tradera/apps/tradera/components/notifications-side-sheet/notifications-api';
import type { NotificationApiDto } from 'tradera/state/services/types/webapi-member-generated';
import Shimmer from 'tradera/legacy/static/components/shimmer/shimmer';
import ALink from 'tradera/components/alink/alink';
import { classNames } from 'tradera/utils/class-names';
import { useLocalizedPriceFormatter } from 'tradera/localization/use-localized-price-formatter';
import { useResetState } from 'tradera/apps/tradera/components/use-reset-state';
import { useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import NotificationsBell from './notifications-bell.svg?jsx';

import styles from './notifications-side-sheet.module.scss';
import { Heading } from 'tradera/components/atoms/heading/heading';
import { Text } from 'tradera/components/atoms/text/text';
import { useTimeSinceShortFormat } from 'tradera/apps/tradera/components/notifications-side-sheet/use-time-since-short-format';
import { logger } from 'tradera/legacy/static/packages/logger';
import { CurrencyRateType } from 'tradera/state/multi-currency/reducer';

export const NotificationsSideSheet = () => {
    const { t } = useTranslator();

    const dispatch = useAppDispatch();

    const { isOpen } = useAppSelector(
        (state) => state.ui.notificationsSideSheet
    );

    const handleDismiss = () => {
        dispatch(closeNotificationsSideSheet());
    };

    return (
        <SideMenu show={isOpen} onDismiss={handleDismiss} width="30rem">
            <ScrollableModalContent
                padding={false}
                innerClassName="py-sm-1"
                onClose={handleDismiss}
                header={
                    <HeaderWithCloseButton
                        onClose={handleDismiss}
                        title={t('notifications_sidesheet_header')}
                        border
                    />
                }
                className="bg-white">
                <NotificationItems handleDismiss={handleDismiss} />
            </ScrollableModalContent>
        </SideMenu>
    );
};

type NotificationItemsProps = {
    handleDismiss: () => void;
};
const NotificationItems = ({ handleDismiss }: NotificationItemsProps) => {
    const {
        isLoading,
        isFetching,
        notifications,
        isAllItemsFetched,
        nextPage
    } = useNotificationsData();
    const { ref: loadingMoreRef, inView } = useInView();

    useEffect(() => {
        if (isFetching || !inView || isAllItemsFetched) return;

        nextPage();
    }, [inView, isFetching, isAllItemsFetched, nextPage]);

    return (
        <>
            {isLoading ? (
                <LoadingIcon className="icon icon-xl icon-gray-400 icon-spin m-auto" />
            ) : notifications && notifications.length > 0 ? (
                <>
                    <ul className="plain-list">
                        {notifications.map((notification, i) => (
                            <li
                                key={i}
                                className={classNames(
                                    'px-2 px-sm-3',
                                    styles.notification
                                )}>
                                <NotificationSwitch
                                    notification={notification}
                                    onNavigate={handleDismiss}
                                />
                            </li>
                        ))}
                    </ul>
                    {isFetching && (
                        <div className="text-center">
                            <LoadingIcon className="icon icon-lg icon-spin m-auto" />
                        </div>
                    )}
                    <div ref={loadingMoreRef} />
                </>
            ) : (
                <NotificationsEmptyState />
            )}
        </>
    );
};

const NotificationsEmptyState = () => {
    const { t } = useTranslator();
    return (
        <div className="d-flex flex-column gap-3 m-3">
            <NotificationsBell
                className="mt-3 mb-1 mx-auto"
                style={{ height: 123, width: 95.11 }}
            />
            <Heading as="h5" branded align="center">
                {t('notificationsSideSheet_emptyState_title')}
            </Heading>
            <Text align="center">
                {t('notificationsSideSheet_emptyState_description')}
            </Text>
        </div>
    );
};

enum NotificationTypes {
    ItemClosedWithoutBuyer = 'ItemClosedWithoutBuyer',
    ItemNewLeadingBid = 'ItemNewLeadingBid',
    ItemSold = 'ItemSold',
    ItemWon = 'ItemWon',
    MemberOutbidded = 'MemberOutbidded',
    LeadingAutoBidPlaced = 'LeadingAutoBidPlaced',
    BuyerPaid = 'BuyerPaid',
    PurchaseOrderUnpaid = 'PurchaseOrderUnpaid',
    ShipmentSent = 'ShipmentSent',
    PurchaseOfferNoLongerHighestAmount = 'PurchaseOfferNoLongerHighestAmount',
    WishlistItemPriceLowered = 'WishlistItemPriceLowered',
    WishlistedShopItemReminder = 'WishlistedShopItemReminder',
    FeedbackReminder = 'FeedbackReminder',
    WishlistItemsEndingToday = 'WishlistItemsEndingToday',
    WishlistItemResell = 'WishlistItemResell'
}

const NotificationsWithBuyerRate = new Set([
    NotificationTypes.ItemWon,
    NotificationTypes.MemberOutbidded,
    NotificationTypes.LeadingAutoBidPlaced,
    NotificationTypes.PurchaseOrderUnpaid,
    NotificationTypes.PurchaseOfferNoLongerHighestAmount,
    NotificationTypes.WishlistItemPriceLowered,
    NotificationTypes.WishlistedShopItemReminder,
    NotificationTypes.WishlistItemsEndingToday
]);

type NotificationSwitchProps = {
    notification: NotificationApiDto;
    onNavigate: () => void;
};
const NotificationSwitch = ({
    notification,
    onNavigate
}: NotificationSwitchProps) => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const localizedPriceFormatter = useLocalizedPriceFormatter(
        NotificationsWithBuyerRate.has(notification.type as NotificationTypes)
            ? CurrencyRateType.Buyer
            : CurrencyRateType.Seller
    );
    const isMobile = useIsMobile();
    try {
        const { title: titleFull, amount: amountSek } = notification;
        const title = truncate(titleFull, isMobile ? 20 : 40);
        const amount = localizedPriceFormatter(amountSek);
        switch (notification.type) {
            case NotificationTypes.ItemClosedWithoutBuyer:
                return (
                    <ItemNotification
                        notification={notification}
                        onNavigate={onNavigate}>
                        <Trans
                            i18nKey="notificationsSideSheet_itemClosedWithoutBuyer_Trans"
                            values={{ title }}
                            components={{ strong: <strong /> }}
                        />
                    </ItemNotification>
                );

            case NotificationTypes.ItemNewLeadingBid:
                return (
                    <ItemNotification
                        notification={notification}
                        onNavigate={onNavigate}>
                        <Trans
                            i18nKey="notificationsSideSheet_itemNewLeadingBid_Trans"
                            values={{ amount, title }}
                            components={{ strong: <strong /> }}
                        />
                    </ItemNotification>
                );

            case NotificationTypes.ItemSold:
                return (
                    <ItemNotification
                        notification={notification}
                        onNavigate={onNavigate}>
                        <Trans
                            i18nKey="notificationsSideSheet_itemSold_Trans"
                            values={{ title, amount }}
                            components={{ strong: <strong /> }}
                        />
                    </ItemNotification>
                );

            case NotificationTypes.ItemWon:
                return (
                    <ItemNotification
                        notification={notification}
                        onNavigate={onNavigate}>
                        <Trans
                            i18nKey="notificationsSideSheet_itemWon_Trans"
                            values={{ title, amount }}
                            components={{ strong: <strong /> }}
                        />
                    </ItemNotification>
                );

            case NotificationTypes.LeadingAutoBidPlaced:
                return (
                    <ItemNotification
                        notification={notification}
                        onNavigate={onNavigate}>
                        <Trans
                            i18nKey="notificationsSideSheet_leadingAutoBidPlaced_Trans"
                            values={{ title, amount }}
                            components={{ strong: <strong /> }}
                        />
                    </ItemNotification>
                );

            case NotificationTypes.MemberOutbidded:
                return (
                    <ItemNotification
                        notification={notification}
                        onNavigate={onNavigate}>
                        <Trans
                            i18nKey="notificationsSideSheet_memberOutbidded_Trans"
                            values={{ title, amount }}
                            components={{ strong: <strong /> }}
                        />
                    </ItemNotification>
                );

            case NotificationTypes.BuyerPaid: {
                const notificationData = notification.buyerPaidData!;
                const { link: buyerPaidLink, key: buyerPaidKey } = (() => {
                    switch (notificationData.shipmentBookingType) {
                        case 'Takeaway':
                            return {
                                link: `/my/order/${notificationData.purchaseOrderId}`,
                                key: 'notificationsSideSheet_buyerPaid_takeaway_Trans'
                            };
                        case 'Automatic':
                            return {
                                link: `/my/shipping?purchaseOrderId=${notificationData.purchaseOrderId}`,
                                key: 'notificationsSideSheet_buyerPaid_automatic_Trans'
                            };
                        case 'Manual':
                        default:
                            return {
                                link: `/my/shipping?purchaseOrderId=${notificationData.purchaseOrderId}`,
                                key: 'notificationsSideSheet_buyerPaid_manual_Trans'
                            };
                    }
                })();
                return (
                    <ItemNotification
                        notification={notification}
                        href={toLocalizedUrl(buyerPaidLink)}
                        onNavigate={onNavigate}>
                        <Trans
                            i18nKey={buyerPaidKey}
                            values={{
                                title,
                                plusAdditionalItems: getPlusAdditionalItemsText(
                                    t,
                                    notificationData.numberOfItems
                                )
                            }}
                            components={{ strong: <strong /> }}
                        />
                    </ItemNotification>
                );
            }

            case NotificationTypes.PurchaseOrderUnpaid: {
                const notificationData = notification.purchaseOrderUnpaidData!;
                return (
                    <ItemNotification
                        notification={notification}
                        href={toLocalizedUrl(
                            `/shopping/ordercheckout/${notificationData.purchaseOrderId}`
                        )}
                        onNavigate={onNavigate}>
                        <Trans
                            i18nKey="notificationsSideSheet_purchaseOrderUnpaid_Trans"
                            values={{
                                title,
                                plusAdditionalItems: getPlusAdditionalItemsText(
                                    t,
                                    notificationData.numberOfItems
                                )
                            }}
                            components={{ strong: <strong /> }}
                        />
                    </ItemNotification>
                );
            }

            case NotificationTypes.ShipmentSent: {
                const notificationData = notification.shipmentSentData!;
                return (
                    <ItemNotification
                        notification={notification}
                        href={toLocalizedUrl(
                            `/my/order/${notificationData.purchaseOrderId}`
                        )}
                        onNavigate={onNavigate}>
                        <Trans
                            i18nKey="notificationsSideSheet_shipmentSent_Trans"
                            values={{
                                title,
                                plusAdditionalItems: getPlusAdditionalItemsText(
                                    t,
                                    notificationData.numberOfItems
                                )
                            }}
                            components={{ strong: <strong /> }}
                        />
                    </ItemNotification>
                );
            }

            case NotificationTypes.PurchaseOfferNoLongerHighestAmount:
                return (
                    <ItemNotification
                        notification={notification}
                        onNavigate={onNavigate}>
                        <Trans
                            i18nKey="notificationsSideSheet_purchaseOfferNoLongerHighestAmount_Trans"
                            values={{ title }}
                            components={{ strong: <strong /> }}
                        />
                    </ItemNotification>
                );

            case NotificationTypes.WishlistItemPriceLowered: {
                const notificationData =
                    notification.wishlistItemPriceLoweredData!;
                return (
                    <ItemNotification
                        notification={notification}
                        onNavigate={onNavigate}>
                        <Trans
                            i18nKey="notificationsSideSheet_wishlistItemPriceLowered_Trans"
                            values={{
                                title,
                                amount: localizedPriceFormatter(
                                    notificationData.newPrice
                                )
                            }}
                            components={{ strong: <strong /> }}
                        />
                    </ItemNotification>
                );
            }

            case NotificationTypes.WishlistedShopItemReminder:
                return (
                    <ItemNotification
                        notification={notification}
                        onNavigate={onNavigate}>
                        <Trans
                            i18nKey="notificationsSideSheet_wishlistedShopItemReminder_Trans"
                            values={{
                                title
                            }}
                            components={{ strong: <strong /> }}
                        />
                    </ItemNotification>
                );

            case NotificationTypes.FeedbackReminder: {
                const notificationData = notification.feedbackReminderData!;
                return notificationData.isSeller ? (
                    <ItemNotification
                        notification={notification}
                        href={toLocalizedUrl('/my/leave-feedback/seller')}
                        onNavigate={onNavigate}>
                        <Trans
                            i18nKey="notificationsSideSheet_feedbackReminder_seller_Trans"
                            values={{
                                title
                            }}
                            components={{ strong: <strong /> }}
                        />
                    </ItemNotification>
                ) : (
                    <ItemNotification
                        notification={notification}
                        href={toLocalizedUrl('/my/leave-feedback/buyer')}
                        onNavigate={onNavigate}>
                        <Trans
                            i18nKey="notificationsSideSheet_feedbackReminder_buyer_Trans"
                            values={{
                                title
                            }}
                            components={{ strong: <strong /> }}
                        />
                    </ItemNotification>
                );
            }

            case NotificationTypes.WishlistItemsEndingToday: {
                const notificationData =
                    notification.wishListItemsEndingTodayData!;
                return (
                    <ItemNotification
                        notification={notification}
                        href={toLocalizedUrl('/my/wish-list')}
                        onNavigate={onNavigate}>
                        <Trans
                            i18nKey="notificationsSideSheet_wishListItemsEndingToday_Trans"
                            values={{
                                count: notificationData.numberOfItems
                            }}
                            components={{ strong: <strong /> }}
                        />
                    </ItemNotification>
                );
            }

            case NotificationTypes.WishlistItemResell:
                return (
                    <ItemNotification
                        notification={notification}
                        onNavigate={onNavigate}>
                        <Trans
                            i18nKey="notificationsSideSheet_wishlistItemResell_Trans"
                            values={{
                                title
                            }}
                            components={{ strong: <strong /> }}
                        />
                    </ItemNotification>
                );

            default:
                logger(`Unkown NotificationType: ${notification.type}`);
                return null;
        }
    } catch (e) {
        logger(e);
        return null;
    }
};

const getPlusAdditionalItemsText = (
    t: TranslationFunction,
    totalCount: number | undefined
) => {
    if (totalCount && totalCount > 1) {
        return t('notificationsSideSheet_plusAdditionalItems', {
            count: totalCount - 1
        });
    }

    return '';
};

type ItemNotificationProps = React.PropsWithChildren<{
    notification: NotificationApiDto;
    onNavigate: () => void;
    href?: string;
}>;
const ItemNotification = ({
    notification,
    children,
    onNavigate,
    href
}: ItemNotificationProps) => {
    const { toLocalizedUrl } = useUrlLocalizer();
    return (
        <BaseNotification
            onNavigate={onNavigate}
            href={href ?? toLocalizedUrl(`/item/${notification.itemId}`)}
            timeStamp={notification.timeStamp}
            isRead={notification.isRead}
            imageSrc={notification.mainImageTemplate?.replace(
                '/{format}/',
                '/small-fit/'
            )}
            imageTitle={notification.title ?? undefined}>
            {children}
        </BaseNotification>
    );
};

type BaseNotificationProps = React.PropsWithChildren<{
    onNavigate: () => void;
    href: string;
    timeStamp: string;
    isRead: boolean;
    imageSrc?: string;
    imageTitle?: string;
}>;
const BaseNotification = ({
    children,
    onNavigate,
    href,
    timeStamp,
    isRead,
    imageSrc,
    imageTitle
}: BaseNotificationProps) => {
    const dateText = useTimeSinceShortFormat(timeStamp);

    return (
        <>
            <ALink
                className="d-flex flex-grow-1 py-2 text-decoration-none border-bottom"
                href={href}
                onClick={onNavigate}>
                <img
                    loading="lazy"
                    onLoad={() => <Shimmer width={63} height={63} />}
                    className="flex-shrink-0 mr-2"
                    style={{
                        width: '4.5rem',
                        height: '4.5rem',
                        objectFit: 'cover',
                        filter: 'brightness(0.98)'
                    }}
                    src={imageSrc}
                    title={imageTitle}
                    alt={imageTitle}
                />
                <div
                    className={classNames(
                        'flex-grow-1',
                        styles.notificationDescription
                    )}>
                    {children}
                </div>
                <div className="d-flex flex-column align-items-end ml-1">
                    <div>
                        <span className="size-oslo text-nowrap text-gray-600">
                            {dateText}
                        </span>
                    </div>
                    <div>
                        <DotIcon
                            className={classNames(
                                'icon icon-sm icon-danger',
                                isRead && 'invisible'
                            )}
                        />
                    </div>
                    <div>
                        <ArrowRightIcon className="icon icon-sm icon-gray-800" />
                    </div>
                </div>
            </ALink>
        </>
    );
};

const truncate = (value: string | null | undefined, length: number) =>
    (value?.length ?? 0) > length
        ? value?.substring(0, length - 1) + '\u2026'
        : value;

export const useNotificationsData = (skip: boolean = false) => {
    // By using the data from endpoints we can utilize SSR:d data, but then we
    // need to call the hook below anyway to get in-client interactivity like pagination.
    const {
        currentData: {
            page: latestPageMaybe,
            items: notifications = [],
            totalCount = 0
        } = {}
    } = endpoints.getNotificationsEndless.useQueryState({ page: 1 });
    const latestPage = latestPageMaybe ?? 1;
    const [page, setPage] = useResetState(latestPage);

    const { isLoading, isFetching } = useGetNotificationsEndlessQuery(
        { page },
        { skip }
    );
    const nextPage = useCallback(() => {
        setPage((p) => p + 1);
    }, [setPage]);

    const isAllItemsFetched =
        latestPage * NOTIFICATIONS_ENDLESS_PAGESIZE >= totalCount;

    return {
        notifications,
        isLoading,
        isFetching,
        isAllItemsFetched,
        page,
        nextPage
    };
};
